import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Skeleton,
} from "@mui/material";
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import { isBehaviorIncidentEntry, LOG_TYPES } from "../../constants/logTypeRegistry";

import { useUnifiedTimelineData } from "../../hooks/useUnifiedTimelineData";
import { useRole } from "../../contexts/RoleContext";
import { CATEGORY_COLORS } from "../../constants/categoryColors";
import { trackRenderDebug, useMountDebug } from "../../utils/renderDebug";
import { getCareLogTone } from "../UI/careLogCardTheme";
import { buildTimelineDaySnapshot } from "./utils/timelineDaySnapshot";
import { buildTimelineEntryPresentation } from "./utils/timelinePresentationUtils";
import { buildDailyNarrativeBlocks } from "./utils/timelineNarrativeGrouping";
import {
  buildTimelinePresentationItemsForDate,
} from "./utils/timelinePresentationCompositor";
import { selectMedicationChangeTimelinePatterns } from './utils/medicationTimelinePatternDetector';
import colors from "../../assets/theme/colors";
import useTimelineDeleteFlow from "./hooks/useTimelineDeleteFlow";
import { auth } from "../../services/firebase";
import TimelineDateNavigator from "../UI/TimelineDateNavigator";
import { getChildAccentTheme } from '../Dashboard/shared/childAccentTheme';
import TimelineNarrativeSections, {
  getEmptyTimelineButtonLabel,
  getTimelineDayLabel,
} from "./TimelineNarrativeSections";
import { getTimelineEntryDate } from "../../services/timeline/dateUtils";
import ProfileReviewQueue from "../../features/careSourceProcessing/ProfileReviewQueue";
import ProfileUpdateTimelineSection from "../../features/careSourceProcessing/ProfileUpdateTimelineSection";
import useProfileUpdateTimelineReceipts from "../../features/careSourceProcessing/useProfileUpdateTimelineReceipts";
import { buildProfileReceiptCalendarEntries } from "../../features/careSourceProcessing/profileUpdateTimelineReceiptModel";
import { getCalendarDateKey } from "../../utils/calendarDateKey";
import useTimelineSourceSections from '../../hooks/timeline/useTimelineSourceSections';

/**
 * UnifiedTimeline - Main unified timeline component
 * Combines incidents, daily logs, daily habits, and follow-ups for a single day
 *
 * @param {Object} props
 * @param {Object} props.child - Child object
 * @param {Date} props.selectedDate - Date to show timeline for
 * @param {Object} props.filters - Active filters for timeline
 * @param {Function} props.onFiltersChange - Callback when filters change
 * @param {boolean} props.showFilters - Whether to show filter controls
 */
const UnifiedTimeline = ({
  child,
  selectedDate,
  filters = {},
  onFiltersChange,
  onEmptyStateClick,
  showFilters = true,
  showDaySummary = true,
  mobileTimeLayout = false,
  focusEntryId = null,
  calendarEntries = [],
  onOpenDatePicker = null,
  onDateChange = null,
  onOpenPatterns = null,
  onAddEntryForDate = null,
  profileUpdateReceipts: providedProfileUpdateReceipts = null,
  onReviewAppointment = null,
}) => {
  useMountDebug('UnifiedTimeline');
  const getContrastText = React.useCallback((hexColor) => {
    const normalized = String(hexColor || '').replace('#', '');
    const hex = normalized.length === 3
      ? normalized.split('').map((char) => `${char}${char}`).join('')
      : normalized;

    if (!/^[0-9a-fA-F]{6}$/.test(hex)) {
      return '#FFFFFF';
    }

    const red = parseInt(hex.slice(0, 2), 16);
    const green = parseInt(hex.slice(2, 4), 16);
    const blue = parseInt(hex.slice(4, 6), 16);
    const brightness = (red * 299 + green * 587 + blue * 114) / 1000;

    return brightness > 160 ? '#1F2937' : '#FFFFFF';
  }, []);

  const resolveCategoryColor = React.useCallback((entry) => {
    if (entry.incidentStyle || entry.entryType === 'incident' || isBehaviorIncidentEntry(entry)) {
      const incidentColor = entry.incidentCategoryColor || LOG_TYPES.behavior.palette.dot || colors.app.timeline.incident;
      return {
        bg: getCareLogTone('behavior')?.bg || colors.app.cards.background,
        text: incidentColor,
        border: getCareLogTone('behavior')?.border || colors.app.cards.border,
        dot: incidentColor,
      };
    }

    if (entry.collection === 'incidents') {
      const incidentColor = entry.incidentCategoryColor || colors.app.timeline.incident;
      return {
        bg: getCareLogTone('behavior')?.bg || colors.app.cards.background,
        text: incidentColor,
        border: getCareLogTone('behavior')?.border || colors.app.cards.border,
        dot: incidentColor,
      };
    }

    if (entry.collection === 'dailyCare') {
      const habitColor = entry.categoryColor || colors.app.timeline.dailyHabit;
      return {
        bg: getCareLogTone('activity')?.bg || colors.app.cards.background,
        text: habitColor,
        border: getCareLogTone('activity')?.border || colors.app.cards.border,
        dot: habitColor,
      };
    }

    if (entry.isImportantMoment) {
      return {
        ...CATEGORY_COLORS.importantMoment,
        bg: CATEGORY_COLORS.importantMoment.bg || colors.app.cards.background,
        border: CATEGORY_COLORS.importantMoment.border || colors.app.cards.border,
      };
    }

    const categoryKey = CATEGORY_COLORS[entry.category]
      ? entry.category
      : CATEGORY_COLORS[entry.type]
        ? entry.type
        : 'log';
    const tone = getCareLogTone(categoryKey);

    return {
      ...tone,
      bg: tone?.bg || colors.app.cards.background,
      border: tone?.border || colors.app.cards.border,
    };
  }, []);

  const { getUserRoleForChild } = useRole();

  // Get user role for this child
  const userRole = getUserRoleForChild?.(child?.id) || null;
  const childAccent = React.useMemo(() => getChildAccentTheme(child || {}), [child]);
  const { receipts: loadedProfileUpdateReceipts } = useProfileUpdateTimelineReceipts({
    childId: child?.id || '',
    viewerRole: userRole,
    enabled: !Array.isArray(providedProfileUpdateReceipts),
  });
  const profileUpdateReceipts = Array.isArray(providedProfileUpdateReceipts)
    ? providedProfileUpdateReceipts
    : loadedProfileUpdateReceipts;
  const selectedDateKey = React.useMemo(
    () => getCalendarDateKey(selectedDate),
    [selectedDate]
  );
  const selectedProfileUpdateReceipts = React.useMemo(
    () => profileUpdateReceipts.filter((receipt) => receipt.dateKey === selectedDateKey),
    [profileUpdateReceipts, selectedDateKey]
  );
  const profileReceiptCalendarEntries = React.useMemo(
    () => buildProfileReceiptCalendarEntries(profileUpdateReceipts),
    [profileUpdateReceipts]
  );
  const profileReceiptSourceIds = React.useMemo(
    () => profileUpdateReceipts.flatMap((receipt) => [
      receipt.sourceKey,
      ...(receipt.sources || []).flatMap((source) => [source.id, source.path]),
    ]).filter(Boolean),
    [profileUpdateReceipts]
  );
  const prefetchDates = React.useMemo(() => {
    if (!mobileTimeLayout || !(selectedDate instanceof Date) || Number.isNaN(selectedDate.getTime())) {
      return [];
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return [-2, -1, 1, 2]
      .map((offset) => {
        const date = new Date(selectedDate);
        date.setDate(selectedDate.getDate() + offset);
        date.setHours(0, 0, 0, 0);
        return date;
      })
      .filter((date) => date <= today);
  }, [mobileTimeLayout, selectedDate]);
  // Fetch unified timeline data
  const { entries, loading, error } = useUnifiedTimelineData(
    child?.id,
    selectedDate,
    filters,
    { prefetchDates }
  );
  const { pendingAppointmentCalendarEntries, hasPendingContent, sections: sourceSections } = useTimelineSourceSections({
    child, userRole, selectedDateKey, entries, onReviewAppointment,
  });
  const sharedCalendarEntries = React.useMemo(
    () => [...calendarEntries, ...profileReceiptCalendarEntries, ...pendingAppointmentCalendarEntries],
    [calendarEntries, pendingAppointmentCalendarEntries, profileReceiptCalendarEntries]
  );
  trackRenderDebug('UnifiedTimeline', {
    childId: child?.id || 'none',
    loading,
    entryCount: entries.length,
    selectedDate: selectedDate?.toDateString?.() || 'n/a',
  });
  const mobileTimelineMinHeight = { xs: 220, md: 'auto' };
  const [localEntryUpdates] = React.useState({});
  const {
    actionLoadingId,
    deletedEntryIds,
    handleDeleteEntry,
    deleteConfirmation,
  } = useTimelineDeleteFlow({ childId: child?.id });

  const handleStartEdit = React.useCallback((entry, entryConfig = null) => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('captureez:timeline-edit-request', {
        detail: {
          entry,
          entryConfig,
        },
      }));
    }
  }, []);

  const visibleEntries = React.useMemo(() => {
    // Shared pipeline already removes deleted entries; this only hides the just-deleted row until refresh lands.
    return entries
      .filter((entry) => !deletedEntryIds[entry.id])
      .map((entry) => {
        const localUpdate = localEntryUpdates[entry.id];
        return localUpdate ? { ...entry, ...localUpdate } : entry;
      });
  }, [deletedEntryIds, entries, localEntryUpdates]);
  const medicationTimingPatterns = React.useMemo(() => selectMedicationChangeTimelinePatterns(visibleEntries), [visibleEntries]);
  const medicationTimingPatternsByAnchorId = React.useMemo(() => medicationTimingPatterns.reduce((acc, pattern) => {
    const anchorId = pattern?.medicationChange?.entryId;
    if (!anchorId) {
      return acc;
    }

    if (!acc[anchorId]) {
      acc[anchorId] = [];
    }

    acc[anchorId].push(pattern);
    return acc;
  }, {}), [medicationTimingPatterns]);
  const visibleEntriesWithPatterns = React.useMemo(() => visibleEntries.map((entry) => {
    const patternsForEntry = medicationTimingPatternsByAnchorId[entry.id];
    return patternsForEntry?.length
      ? { ...entry, medicationTimingPatterns: patternsForEntry }
      : entry;
  }), [medicationTimingPatternsByAnchorId, visibleEntries]);

  React.useEffect(() => {
    if (!focusEntryId) {
      return undefined;
    }

    let highlightedElement = null;
    let highlightTimer = null;
    const timer = window.setTimeout(() => {
      const entryElement = document.getElementById(`timeline-entry-${focusEntryId}`);
      if (entryElement) {
        highlightedElement = entryElement;
        entryElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
        entryElement.style.transition = 'outline-color 400ms ease, box-shadow 400ms ease';
        entryElement.style.outline = '3px solid rgba(78, 164, 140, 0.42)';
        entryElement.style.outlineOffset = '3px';
        entryElement.style.boxShadow = '0 10px 28px rgba(46, 91, 78, 0.16)';
        highlightTimer = window.setTimeout(() => {
          entryElement.style.outlineColor = 'transparent';
          entryElement.style.boxShadow = '';
        }, 2200);
      }
    }, 50);

    return () => {
      window.clearTimeout(timer);
      if (highlightTimer) window.clearTimeout(highlightTimer);
      if (highlightedElement) {
        highlightedElement.style.outline = '';
        highlightedElement.style.outlineOffset = '';
        highlightedElement.style.boxShadow = '';
        highlightedElement.style.transition = '';
      }
    };
  }, [focusEntryId, visibleEntries.length]);

  const getEntryPresentation = React.useCallback((entry) => (
    buildTimelineEntryPresentation({
      entry,
      child,
      getContrastText,
      resolveCategoryColor,
    })
  ), [child, getContrastText, resolveCategoryColor]);

  const presentationItems = React.useMemo(() => (
    buildTimelinePresentationItemsForDate({
      entries: visibleEntriesWithPatterns,
      calendarEntries: sharedCalendarEntries,
      selectedDate,
    })
  ), [selectedDate, sharedCalendarEntries, visibleEntriesWithPatterns]);
  const groupedNarrativeBlocks = React.useMemo(() => (
    buildDailyNarrativeBlocks({
      entries: presentationItems,
      getEntryPresentation,
      getEntryDateFn: getTimelineEntryDate,
    })
  ), [getEntryPresentation, presentationItems]);

  const daySnapshot = React.useMemo(
    () => buildTimelineDaySnapshot(presentationItems),
    [presentationItems]
  );
  const addButtonLabel = React.useMemo(() => {
    return `Add to ${getTimelineDayLabel(selectedDate)}`;
  }, [selectedDate]);
  const emptyTimelineButtonLabel = React.useMemo(
    () => getEmptyTimelineButtonLabel(selectedDate),
    [selectedDate]
  );
  const personName = child?.preferredName || child?.name || child?.firstName || 'this person';
  const profileUpdateReceiptSection = (
    <ProfileUpdateTimelineSection
      receipts={selectedProfileUpdateReceipts}
      child={child}
      currentUserRole={userRole}
    />
  );
  const profileReviewDialog = (
    <ProfileReviewQueue
      childId={child?.id || ''}
      personName={personName}
      dialogOnly
      notifyTimelineChanges={false}
    />
  );
  const timelineBeforeSections = (
    <>
      {profileReviewDialog}
      {deleteConfirmation}
      {sourceSections}
    </>
  );
  const handleAddEntryForSelectedDate = React.useCallback((event) => {
    onAddEntryForDate?.(selectedDate, event);
  }, [onAddEntryForDate, selectedDate]);

  // Note: entries are already pre-filtered; grouping by period is optional and not used here.


  // Loading state
  if (loading) {
    return (
        <Box
          sx={{
            minHeight: mobileTimelineMinHeight,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            gap: 0.9,
            pt: 0.5,
          }}
        >
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
          <CircularProgress size={24} />
        </Box>
        {[0, 1, 2].map((item) => (
          <Box
            key={item}
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
            }}
          >
            <Skeleton
              variant="circular"
              width={20}
              height={20}
              sx={{ flex: '0 0 auto', mt: 1.5 }}
            />
            <Skeleton
              variant="rounded"
              height={78}
              sx={{ flex: 1, borderRadius: 0.5 }}
            />
          </Box>
        ))}
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load timeline data: {error.message}
      </Alert>
    );
  }

  if (mobileTimeLayout) {
    return (
      <Box sx={{ px: { xs: 0, md: 0.5 }, pt: 0.25, pb: onAddEntryForDate ? { xs: 'calc(9rem + env(safe-area-inset-bottom, 0px))', md: 0 } : 0, width: '100%', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
        <Box sx={{ px: { xs: 2, md: 2.25 }, mb: 1.4 }}>
          <TimelineDateNavigator
            selectedDate={selectedDate}
            onDateChange={onDateChange}
            onOpenCalendar={onOpenDatePicker}
            entries={sharedCalendarEntries}
            dayWindow={4}
            inlineCalendar
            showJumpToday
          />
        </Box>

        <TimelineNarrativeSections
          beforeSections={timelineBeforeSections}
          afterSections={profileUpdateReceiptSection}
          actionMenuDisabled={Boolean(actionLoadingId)}
          currentUserId={auth.currentUser?.uid || null}
          currentUserRole={userRole}
          child={child}
          getEntryPresentation={getEntryPresentation}
          mobileTimelineMinHeight={mobileTimelineMinHeight}
          groupedNarrativeBlocks={groupedNarrativeBlocks}
          hasSupplementalTimelineContent={selectedProfileUpdateReceipts.length > 0 || hasPendingContent}
          profileReceiptSourceIds={profileReceiptSourceIds}
          daySnapshot={daySnapshot}
          showDaySummary={showDaySummary}
          onDeleteEntry={handleDeleteEntry}
          onEditEntry={handleStartEdit}
          onEmptyStateClick={onEmptyStateClick || (onAddEntryForDate ? handleAddEntryForSelectedDate : null)}
          selectedDate={selectedDate}
          accent={childAccent}
        />
        {onAddEntryForDate ? (
          <Box
            sx={{
              position: { xs: 'fixed', md: 'sticky' },
              bottom: { xs: 'calc(82px + env(safe-area-inset-bottom, 0px))', md: 0 },
              left: { xs: 0, md: 'auto' },
              right: { xs: 0, md: 'auto' },
              zIndex: 20,
              px: { xs: 2, md: 2.25 },
              pt: 1,
              pb: { xs: 1.2, md: 'max(14px, env(safe-area-inset-bottom))' },
              background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, #FFFFFF 42%)',
            }}
          >
            <Button
              fullWidth
              variant="contained"
              startIcon={visibleEntries.length > 0 ? <AddIcon /> : undefined}
              onClick={handleAddEntryForSelectedDate}
              sx={{
                minHeight: 58,
                maxWidth: { xs: 760, md: 'none' },
                mx: 'auto',
                borderRadius: '20px',
                textTransform: 'none',
                fontSize: { xs: '1rem', sm: '1.05rem' },
                fontWeight: 850,
                color: colors.semantic.sageActionInk,
                bgcolor: colors.semantic.sageAction,
                boxShadow: `0 10px 24px ${alpha(colors.semantic.sageAction, 0.24)}`,
                '&:hover': {
                  bgcolor: colors.semantic.sageActionHover,
                  boxShadow: `0 12px 28px ${alpha(colors.semantic.sageAction, 0.3)}`,
                },
              }}
            >
              {visibleEntries.length > 0 ? addButtonLabel : emptyTimelineButtonLabel}
            </Button>
          </Box>
        ) : null}
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
      <TimelineNarrativeSections
        beforeSections={timelineBeforeSections}
        afterSections={profileUpdateReceiptSection}
        actionMenuDisabled={Boolean(actionLoadingId)}
        currentUserId={auth.currentUser?.uid || null}
        currentUserRole={userRole}
        child={child}
        getEntryPresentation={getEntryPresentation}
        mobileTimelineMinHeight={mobileTimelineMinHeight}
        desktopFeed
        daySnapshot={daySnapshot}
        showDaySummary={showDaySummary}
        groupedNarrativeBlocks={groupedNarrativeBlocks}
        hasSupplementalTimelineContent={selectedProfileUpdateReceipts.length > 0 || hasPendingContent}
        profileReceiptSourceIds={profileReceiptSourceIds}
        onDeleteEntry={handleDeleteEntry}
        onEditEntry={handleStartEdit}
        onEmptyStateClick={onEmptyStateClick}
        selectedDate={selectedDate}
        accent={childAccent}
      />
    </Box>
  );
};

export default UnifiedTimeline;
